import { useLocation, useNavigate } from 'react-router-dom';
import ODCustomerInfo from './detail/ODCustomerInfo';
import ODWriteInfo from './detail/ODWriteInfo';
import { UpdateOrderRequest, useOrderStore } from '../../../store/useOrder';
import { useFranchiseStore } from '../../../store/useFranchise';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDate, parseDateTime } from '../../../util/util';
import { toast } from 'react-toastify';
import { useLoginStore } from '../../../store/useLogin';
import OFDPrompt from './detail/OFDPrompt';
import ODReloadText from './detail/ODReloadText';
import Input from '../../form/Input';
import ConfirmDialog from '../../modal/ConfirmDialog';

/**
 * @author taulmax
 * @date 2024.11.06
 * @description 주문 목록 상세 페이지 (주문 목록 상세, 주문 확정 상세 둘다 여기서 함. 상세 및 수정도 여기서 한번에.)
 */
const OrderListDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const locationObj: { list: 'LIST'; fix: 'FIX' } = {
    list: 'LIST',
    fix: 'FIX',
  };
  const whereAmI: 'LIST' | 'FIX' =
    locationObj[location.pathname.split('/')[2] as 'list' | 'fix'];

  const { role } = useLoginStore();
  const isAdminOrCS = role === 'ROLE_ADMIN' || role === 'ROLE_CS';

  const [email, setEmail] = useState<string>('');
  const [tel, setTel] = useState<string>('');
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showPromptSidebar, setShowPromptSidebar] = useState<boolean>(false);

  const { myfranchise, getMyFranchise } = useFranchiseStore();

  // 비즈 프로필 활성화 여부 체크
  const isBizProfileEnabled = useMemo(
    () => Boolean(myfranchise?.bizmProfileKey),
    [myfranchise?.bizmProfileKey]
  );

  const onChangeEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value.replace(/\s/g, ''));
    },
    [setEmail],
  );

  const onChangeTel = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTel(e.target.value.replace(/\s/g, ''));
    },
    [setTel],
  );

  const [customerName1, setCustomerName1] = useState<string>('');
  const [customerSolarLunar1, setCustomerSolarLunar1] = useState<string>('');
  const [customerSex1, setCustomerSex1] = useState<string>('');
  const [customerBirth1, setCustomerBirth1] = useState<string>('');

  const [customerName2, setCustomerName2] = useState<string>('');
  const [customerSolarLunar2, setCustomerSolarLunar2] = useState<string>('');
  const [customerSex2, setCustomerSex2] = useState<string>('');
  const [customerBirth2, setCustomerBirth2] = useState<string>('');

  // 변경 감지
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [initialData, setInitialData] = useState({
    email: '',
    tel: '',
    name1: '',
    solarLunar1: '',
    sex1: '',
    birth1: '',
    name2: '',
    solarLunar2: '',
    sex2: '',
    birth2: '',
  });
  const checkForChanges = useCallback(() => {
    setHasChanges(
      email !== initialData.email ||
        customerName1 !== initialData.name1 ||
        customerSolarLunar1 !== initialData.solarLunar1 ||
        customerSex1 !== initialData.sex1 ||
        customerBirth1 !== initialData.birth1 ||
        customerName2 !== initialData.name2 ||
        customerSolarLunar2 !== initialData.solarLunar2 ||
        customerSex2 !== initialData.sex2 ||
        customerBirth2 !== initialData.birth2 ||
        tel !== initialData.tel
    );
  }, [
    customerBirth1,
    customerBirth2,
    customerName1,
    customerName2,
    customerSex1,
    customerSex2,
    customerSolarLunar1,
    customerSolarLunar2,
    email,
    tel,
    initialData,
  ]);
  const resetChanges = useCallback(() => {
    setEmail(initialData.email);
    setTel(initialData.tel);
    setCustomerName1(initialData.name1);
    setCustomerSolarLunar1(initialData.solarLunar1);
    setCustomerSex1(initialData.sex1);
    setCustomerBirth1(initialData.birth1);
    setCustomerName2(initialData.name2);
    setCustomerSolarLunar2(initialData.solarLunar2);
    setCustomerSex2(initialData.sex2);
    setCustomerBirth2(initialData.birth2);
    setHasChanges(false);
  }, [initialData]);

  const {
    orderDetail,
    getOrderDetail,
    updateOrder,
    deleteOrder,
    confirmOrders,
    retryContractPrompt,
    makePDFMySelf,
  } = useOrderStore();

  // 변경 사항 체크
  useEffect(() => {
    if (orderDetail) {
      checkForChanges();
      getMyFranchise();
    }
  }, [checkForChanges, orderDetail, getMyFranchise]);

  // 상세정보 가져오기
  useEffect(() => {
    const id = location.pathname.split('/')[3];
    getOrderDetail(id);
  }, [getOrderDetail, location.pathname, role]);

  // 가져온 정보 setState
  useEffect(() => {
    if (orderDetail) {
      const { email, telNo, contractCustInfo } = orderDetail;
      setEmail(email);
      setTel(telNo || '');
      setInitialData(data => ({ ...data, email, tel: telNo || '' }));

      const { name, solarType, year, month, day, hour, minute, sexType } =
        contractCustInfo[0];
      setCustomerName1(name);
      setCustomerSolarLunar1(solarType);
      setCustomerSex1(sexType);

      const customerBirth1 =
        hour === null || minute === null
          ? `${year}.${month}.${day}`
          : `${year}.${month}.${day} ${hour}:${minute}`;
      setCustomerBirth1(
        hour === null || minute === null
          ? formatDate(customerBirth1)['yyyy.mm.dd']
          : formatDate(customerBirth1)['yyyy.mm.dd hh:mm']
      );

      setInitialData(data => ({
        ...data,
        name1: name,
        solarLunar1: solarType,
        sex1: sexType,
        birth1:
          hour === null || minute === null
            ? formatDate(customerBirth1)['yyyy.mm.dd']
            : formatDate(customerBirth1)['yyyy.mm.dd hh:mm'],
      }));

      if (contractCustInfo.length === 2) {
        const { name, solarType, year, month, day, hour, minute, sexType } =
          contractCustInfo[1];
        setCustomerName2(name);
        setCustomerSolarLunar2(solarType);
        setCustomerSex2(sexType);

        const customerBirth2 =
          hour === null || minute === null
            ? `${year}.${month}.${day}`
            : `${year}.${month}.${day} ${hour}:${minute}`;
        setCustomerBirth2(
          hour === null || minute === null
            ? formatDate(customerBirth2)['yyyy.mm.dd']
            : formatDate(customerBirth2)['yyyy.mm.dd hh:mm']
        );

        setInitialData(data => ({
          ...data,
          name2: name,
          solarLunar2: solarType,
          sex2: sexType,
          birth2:
            hour === null || minute === null
              ? formatDate(customerBirth2)['yyyy.mm.dd']
              : formatDate(customerBirth2)['yyyy.mm.dd hh:mm'],
        }));
      }
    }
  }, [orderDetail]);

  const onClickUpdate = useCallback(() => {
    // 백엔드 발송 로직과 일치: 전화번호 또는 이메일 중 최소 하나는 필요 (둘 중 하나로 알림톡/메일 발송)
    if (!email && !tel) {
      return toast.warn('전화번호 또는 이메일 중 하나는 입력해주세요.');
    }
    if (!customerName1) {
      return toast.warn('고객정보1: 이름을 입력해주세요.');
    }
    if (!customerBirth1) {
      return toast.warn('고객정보1: 생년월일시를 입력해주세요.');
    }

    const id = orderDetail?.id as string;
    const birth1 = parseDateTime(customerBirth1);
    const orderData: UpdateOrderRequest = {
      email,
      telNo: tel,
      realtimeYn: orderDetail?.realtimeYn,
      contractCustInfo1: {
        name: customerName1,
        solarType: customerSolarLunar1 as 'SOLAR' | 'LUNAR' | 'LUNAR_INTER',
        year: birth1.year,
        month: birth1.month,
        day: birth1.day,
        hour: birth1.hour,
        minute: birth1.minute,
        sexType: customerSex1 as 'M' | 'F',
      },
    };

    if (orderDetail?.contractCustInfo.length === 2) {
      if (!customerName2) {
        return toast.warn('고객정보1: 이름을 입력해주세요.');
      }
      if (!customerBirth2) {
        return toast.warn('고객정보1: 생년월일시를 입력해주세요.');
      }
      const birth2 = parseDateTime(customerBirth2);
      const newOrderData: UpdateOrderRequest = {
        ...orderData,
        contractCustInfo2: {
          name: customerName2,
          solarType: customerSolarLunar2 as 'SOLAR' | 'LUNAR' | 'LUNAR_INTER',
          year: birth2.year,
          month: birth2.month,
          day: birth2.day,
          hour: birth2.hour,
          minute: birth2.minute,
          sexType: customerSex2 as 'M' | 'F',
        },
      };

      updateOrder(id, newOrderData);
    } else {
      updateOrder(id, orderData);
    }
  }, [
    customerBirth1,
    customerBirth2,
    customerName1,
    customerName2,
    customerSex1,
    customerSex2,
    customerSolarLunar1,
    customerSolarLunar2,
    email,
    orderDetail?.contractCustInfo.length,
    orderDetail?.id,
    tel,
    updateOrder,
  ]);

  const onClickDelete = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    const id = location.pathname.replace('/order/list/', '');
    deleteOrder(id, navigate);
  }, [deleteOrder, location.pathname, navigate]);

  const onClickConfirm = useCallback(() => {
    const id = location.pathname.replace('/order/list/', '');
    confirmOrders([id], navigate);
  }, [confirmOrders, location.pathname, navigate]);

  const hasFailedProcess = orderDetail?.contractPromptProcesses.some(
    process => process.status.code === 'FAIL'
  );

  const isAllSuccess = orderDetail?.contractPromptProcesses.every(
    process => process.status.code === 'SUCCESS'
  );

  const onClickRetryAllFailedPrompt = useCallback(() => {
    if (orderDetail) {
      const filteredData = orderDetail.contractPromptProcesses.filter(
        process => process.status.code === 'FAIL'
      );

      if (filteredData.length > 0) {
        const idList = filteredData.map(data => data.id);
        retryContractPrompt(idList);
      }
    }
  }, [orderDetail, retryContractPrompt]);

  const onClickMakePDFMySelf = useCallback(() => {
    if (orderDetail) {
      const idList = [orderDetail.id];
      makePDFMySelf(idList);
    }
  }, [makePDFMySelf, orderDetail]);

  return (
    <div>
      {/* 메인 컨텐츠 */}
      <div>
        <div className="!max-w-6xl !mx-auto !py-6 !px-4">
          {/* 페이지 제목 */}
          <div className="!flex !items-center !justify-between !mb-6">
            <div className="!flex !items-center !gap-3">
              <button
                onClick={() =>
                  navigate(whereAmI === 'LIST' ? '/order/list' : '/order/fix')
                }
                className="!p-2 !rounded-lg hover:!bg-gray-100 !transition-colors"
              >
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  className="!w-4 !h-4 !text-gray-500"
                />
              </button>
              <h1 className="!text-xl !font-semibold !text-gray-900">
                {whereAmI === 'LIST' ? '주문 상세' : '주문 확정 상세'}
              </h1>
            </div>
            {whereAmI === 'FIX' && isAdminOrCS && orderDetail?.contractPromptProcesses && orderDetail.contractPromptProcesses.length > 0 && (
              <button
                onClick={() => setShowPromptSidebar(true)}
                className="!px-4 !py-2 !bg-indigo-600 hover:!bg-indigo-700 !text-white !text-sm !font-medium !rounded-lg !transition-colors !flex !items-center !gap-2"
              >
                <svg className="!w-4 !h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm2 2h8v2H5v-2z" clipRule="evenodd" />
                </svg>
                프롬프트 처리 현황
              </button>
            )}
          </div>

          {/* 컨텐츠 영역 */}
          <div>
              {/* 주요 정보 섹션 */}
              <div className="!grid !grid-cols-1 md:!grid-cols-2 lg:!grid-cols-3 !gap-4 !mb-6">
                {/* 상품 정보 카드 */}
                <div className="!bg-white !rounded-lg !shadow-sm hover:!shadow-md !transition-shadow !duration-200">
                  <div className="!p-5">
                    <div className="!flex !items-center !justify-between !mb-4">
                      <h3 className="!text-sm !font-semibold !text-gray-900">
                        상품 정보
                      </h3>
                      {orderDetail && (
                        <div
                          className={`!inline-flex !items-center !px-2.5 !py-1 !rounded-md !text-xs !font-medium ${
                            orderDetail.confirmYn === 'Y'
                              ? '!bg-green-50 !text-green-700 !ring-1 !ring-inset !ring-green-600/20'
                              : '!bg-amber-50 !text-amber-700 !ring-1 !ring-inset !ring-amber-600/20'
                          }`}
                        >
                          <div
                            className={`!w-1.5 !h-1.5 !rounded-full !mr-1.5 ${
                              orderDetail.confirmYn === 'Y'
                                ? '!bg-green-600'
                                : '!bg-amber-600 !animate-pulse'
                            }`}
                          ></div>
                          {orderDetail.confirmYn === 'Y'
                            ? '확정완료'
                            : '확정대기'}
                        </div>
                      )}
                    </div>
                    <div className="!space-y-3">
                      {orderDetail ? (
                        <>
                          <div>
                            <p className="!text-xs !text-gray-500">주문번호</p>
                            <p className="!text-sm !font-mono !text-gray-900 !mt-0.5">
                              {orderDetail.id.slice(0, 8).toUpperCase()}
                            </p>
                          </div>
                          <div>
                            <p className="!text-xs !text-gray-500">상품명</p>
                            <p className="!text-sm !font-medium !text-gray-900 !mt-0.5">
                              {orderDetail.product.productName.replace('_notime', '')}
                            </p>
                          </div>
                          <div>
                            <p className="!text-xs !text-gray-500">상품코드</p>
                            <p className="!text-sm !font-mono !text-gray-700 !mt-0.5">
                              {orderDetail.product.productCode.replace('_notime', '')}
                            </p>
                          </div>
                          <div>
                            <p className="!text-xs !text-gray-500 !mb-1">
                              서비스 유형
                            </p>
                            <span
                              className={`!inline-flex !items-center !px-2.5 !py-1 !text-xs !font-medium !rounded-md ${
                                orderDetail.product.custInfoCount === 2
                                  ? '!bg-purple-50 !text-purple-700 !ring-1 !ring-inset !ring-purple-700/10'
                                  : '!bg-blue-50 !text-blue-700 !ring-1 !ring-inset !ring-blue-700/10'
                              }`}
                            >
                              {orderDetail.product.custInfoCount === 2
                                ? '동반 서비스'
                                : '개별 서비스'}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="!space-y-3">
                          <div className="!h-4 !bg-gray-100 !rounded !animate-pulse"></div>
                          <div className="!h-4 !bg-gray-100 !rounded !animate-pulse !w-2/3"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 연락처 정보 카드 */}
                <div className="!bg-white !rounded-lg !shadow-sm hover:!shadow-md !transition-shadow !duration-200">
                  <div className="!p-5">
                    <h3 className="!text-sm !font-semibold !text-gray-900 !mb-4">
                      연락처 정보
                    </h3>
                    <div className="!space-y-3">
                      {orderDetail ? (
                        <>
                          <div>
                            <label className="!block !text-xs !text-gray-600 !mb-1">
                              이메일
                            </label>
                            <Input
                              value={email}
                              onChange={onChangeEmail}
                              width="100%"
                              textAlign="left"
                              placeholder="이메일을 입력하세요"
                            />
                          </div>
                          {(isBizProfileEnabled || role === 'ROLE_ADMIN') && (
                            <div>
                              <label className="!block !text-xs !text-gray-600 !mb-1">
                                연락처
                              </label>
                                  <Input
                                      value={tel}
                                      onChange={onChangeTel}
                                      width="100%"
                                      textAlign="left"
                                      placeholder="전화번호를 입력하세요"
                                  />
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="!space-y-3">
                          <div className="!h-9 !bg-gray-100 !rounded-md !animate-pulse"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 작성 정보 카드 */}
                <div className="!bg-white !rounded-lg !shadow-sm hover:!shadow-md !transition-shadow !duration-200">
                  <div className="!p-5">
                    <h3 className="!text-sm !font-semibold !text-gray-900 !mb-4">
                      작성 정보
                    </h3>
                    <ODWriteInfo />
                  </div>
                </div>
              </div>

              {/* 고객 정보 섹션 */}
              <div className="!bg-white !rounded-lg !shadow-sm hover:!shadow-md !transition-shadow !duration-200">
                <div className="!border-b !border-gray-100 !px-6 !py-4">
                  <h3 className="!text-base !font-semibold !text-gray-900">
                    고객 정보
                  </h3>
                </div>
                <div className="!p-6">
                  <div className="!grid !grid-cols-1 lg:!grid-cols-2 !gap-6">
                    {/* 고객정보 1 */}
                    <div className="!p-4 !bg-gray-50 !rounded-lg !border !border-gray-100">
                      <div className="!flex !items-center !gap-2 !mb-3">
                        <div className="!w-6 !h-6 !bg-blue-100 !rounded-full !flex !items-center !justify-center">
                          <span className="!text-xs !font-semibold !text-blue-600">
                            1
                          </span>
                        </div>
                        <h4 className="!text-sm !font-medium !text-gray-900">
                          첫 번째 고객
                        </h4>
                      </div>
                      <ODCustomerInfo
                        service={1}
                        customerName={customerName1}
                        setCustomerName={setCustomerName1}
                        customerSolarLunar={customerSolarLunar1}
                        setCustomerSolarLunar={setCustomerSolarLunar1}
                        customerSex={customerSex1}
                        setCustomerSex={setCustomerSex1}
                        customerBirth={customerBirth1}
                        setCustomerBirth={setCustomerBirth1}
                        disabled={(role === 'ROLE_USER' || role === 'ROLE_MARKETING') && whereAmI === 'FIX'}
                      />
                    </div>

                    {/* 고객정보 2 */}
                    <div className="!p-4 !bg-gray-50 !rounded-lg !border !border-gray-100">
                      {orderDetail?.contractCustInfo.length === 2 ? (
                        <>
                          <div className="!flex !items-center !gap-2 !mb-3">
                            <div className="!w-6 !h-6 !bg-purple-100 !rounded-full !flex !items-center !justify-center">
                              <span className="!text-xs !font-semibold !text-purple-600">
                                2
                              </span>
                            </div>
                            <h4 className="!text-sm !font-medium !text-gray-900">
                              두 번째 고객
                            </h4>
                          </div>
                          <ODCustomerInfo
                            service={2}
                            customerName={customerName2}
                            setCustomerName={setCustomerName2}
                            customerSolarLunar={customerSolarLunar2}
                            setCustomerSolarLunar={setCustomerSolarLunar2}
                            customerSex={customerSex2}
                            setCustomerSex={setCustomerSex2}
                            customerBirth={customerBirth2}
                            setCustomerBirth={setCustomerBirth2}
                            disabled={(role === 'ROLE_USER' || role === 'ROLE_MARKETING') && whereAmI === 'FIX'}
                          />
                        </>
                      ) : (
                        <div className="!h-full !flex !items-center !justify-center !text-gray-400">
                          <p className="!text-sm">
                            개별 서비스 (1인) 상품입니다
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </div>

      {/* 하단 액션 영역 - 표시할 내용이 있을 때만 렌더링 */}
      {(hasChanges || whereAmI === 'LIST' || (whereAmI === 'FIX' && (!isAllSuccess || isAdminOrCS))) && (
      <div className="!fixed !bottom-0 !left-0 !right-0 !bg-white !border-t !border-gray-200 !shadow-lg !z-20 !pl-64">
        <div
          className={
            whereAmI === 'FIX'
              ? '!max-w-full !mx-auto !px-6 !py-3'
              : '!max-w-6xl !mx-auto !px-6 !py-3'
          }
        >
          <div className="!flex !items-center !justify-between">
            {/* 좌측 상태 정보 */}
            <div className="!flex !items-center !gap-3">
              {hasChanges && (
                <div className="!flex !items-center !gap-2 !text-sm !text-amber-600">
                  <div className="!w-1.5 !h-1.5 !bg-amber-600 !rounded-full !animate-pulse"></div>
                  <span>변경사항이 있습니다</span>
                </div>
              )}
              {whereAmI === 'FIX' && !hasChanges && !isAllSuccess && (
                <div className="!bg-blue-50 !px-3 !py-1.5 !rounded-lg">
                  <ODReloadText />
                </div>
              )}
            </div>

            {/* 우측 액션 버튼들 - 버튼이 있을 때만 표시 */}
            {(hasChanges || whereAmI === 'LIST' || (whereAmI === 'FIX' && isAdminOrCS && (isAllSuccess || hasFailedProcess))) && (
              <div className="!flex !items-center !gap-2">
                {hasChanges ? (
                  <>
                    <button
                      onClick={resetChanges}
                      className="!px-4 !py-2 !bg-white !border !border-gray-200 hover:!bg-gray-50 !text-gray-700 !text-sm !font-medium !rounded-md !transition-colors"
                    >
                      초기화
                    </button>
                    <button
                      onClick={onClickUpdate}
                      className="!px-4 !py-2 !bg-blue-600 hover:!bg-blue-700 !text-white !text-sm !font-medium !rounded-md !transition-colors"
                    >
                      변경사항 저장
                    </button>
                  </>
                ) : (
                  <>
                    {whereAmI === 'LIST' && (
                      <>
                        <button
                          onClick={onClickDelete}
                          className="!px-4 !py-2 !bg-red-600 hover:!bg-red-700 !text-white !text-sm !font-medium !rounded-md !transition-colors"
                        >
                          주문 삭제
                        </button>
                        <button
                          onClick={onClickConfirm}
                          className="!px-4 !py-2 !bg-green-600 hover:!bg-green-700 !text-white !text-sm !font-medium !rounded-md !transition-colors"
                        >
                          주문 확정
                        </button>
                      </>
                    )}
                    {whereAmI === 'FIX' && isAllSuccess && isAdminOrCS && (
                      <button
                        onClick={onClickMakePDFMySelf}
                        className="!px-4 !py-2 !bg-indigo-600 hover:!bg-indigo-700 !text-white !text-sm !font-medium !rounded-md !transition-colors"
                      >
                        PDF 수동 제작
                      </button>
                    )}
                    {hasFailedProcess && isAdminOrCS && (
                      <button
                        onClick={onClickRetryAllFailedPrompt}
                        className="!px-4 !py-2 !bg-amber-600 hover:!bg-amber-700 !text-white !text-sm !font-medium !rounded-md !transition-colors"
                      >
                        실패 프롬프트 재시도
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title="주문 삭제 확인"
        message="정말로 이 주문을 삭제하시겠습니까?
삭제된 주문은 복구할 수 없습니다."
        confirmText="삭제"
        cancelText="취소"
        type="danger"
      />

      {/* 프롬프트 처리 현황 사이드바 */}
      {orderDetail?.contractPromptProcesses && (
        <>
          {/* 배경 오버레이 */}
          <div
            className={`!fixed !inset-0 !bg-black !z-40 !transition-opacity !duration-300 ${
              showPromptSidebar ? '!bg-opacity-50' : '!bg-opacity-0 !pointer-events-none'
            }`}
            onClick={() => setShowPromptSidebar(false)}
          />
          
          {/* 사이드바 */}
          <div className={`!fixed !right-0 !top-0 !bottom-0 !w-[640px] !bg-white !shadow-2xl !z-50 !flex !flex-col !transition-transform !duration-300 !ease-out ${
            showPromptSidebar ? '!translate-x-0' : '!translate-x-full'
          }`}>
            {/* 헤더 */}
            <div className="!px-5 !py-4 !bg-gradient-to-r !from-indigo-50 !to-blue-50 !border-b !border-gray-200">
              <div className="!flex !items-center !justify-between">
                <div>
                  <div className="!flex !items-center !gap-2 !mb-1">
                    <div className="!w-8 !h-8 !bg-indigo-100 !rounded-lg !flex !items-center !justify-center">
                      <svg className="!w-5 !h-5 !text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm2 2h8v2H5v-2z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h2 className="!text-lg !font-semibold !text-gray-900">
                      프롬프트 처리 현황
                    </h2>
                  </div>
                  <p className="!text-sm !text-gray-600 !ml-10">
                    총 {orderDetail.contractPromptProcesses.length}개의 프롬프트 항목
                  </p>
                </div>
                <div className="!flex !items-center !gap-2">
                  {/* 상태 표시 */}
                  {orderDetail.contractPromptProcesses.every(
                    p => p.status.code === 'SUCCESS'
                  ) ? (
                    <div className="!flex !items-center !gap-1.5 !px-3 !py-1.5 !bg-green-100 !text-green-700 !rounded-full !text-xs !font-medium">
                      <svg className="!w-3.5 !h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      모두 완료
                    </div>
                  ) : orderDetail.contractPromptProcesses.some(
                      p => p.status.code === 'FAIL'
                    ) ? (
                    <div className="!flex !items-center !gap-1.5 !px-3 !py-1.5 !bg-red-100 !text-red-700 !rounded-full !text-xs !font-medium">
                      <svg className="!w-3.5 !h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      {
                        orderDetail.contractPromptProcesses.filter(
                          p => p.status.code === 'FAIL'
                        ).length
                      }개 실패
                    </div>
                  ) : (
                    <div className="!flex !items-center !gap-1.5 !px-3 !py-1.5 !bg-yellow-100 !text-yellow-700 !rounded-full !text-xs !font-medium">
                      <div className="!w-3.5 !h-3.5">
                        <svg className="!animate-spin" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 00-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                      </div>
                      처리중
                    </div>
                  )}
                  {/* 닫기 버튼 */}
                  <button
                    onClick={() => setShowPromptSidebar(false)}
                    className="!p-1.5 !rounded-lg hover:!bg-white hover:!bg-opacity-80 !transition-all !duration-200"
                  >
                    <svg className="!w-5 !h-5 !text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* 통계 정보 */}
            <div className="!px-5 !py-3 !bg-gray-50 !border-b !border-gray-200">
              <div className="!grid !grid-cols-4 !gap-3">
                <div className="!text-center">
                  <p className="!text-2xl !font-bold !text-yellow-600">
                    {orderDetail.contractPromptProcesses.filter(p => 
                      p.status.code === 'PENDING' || p.status.code === 'BEFORE_BATCH'
                    ).length}
                  </p>
                  <p className="!text-xs !text-gray-600">대기중</p>
                </div>
                <div className="!text-center !border-x !border-gray-200">
                  <p className="!text-2xl !font-bold !text-blue-600">
                    {orderDetail.contractPromptProcesses.filter(p => 
                      p.status.code === 'IN_PROGRESS' || p.status.code === 'BATCH_PROCESSING' || 
                      p.status.code === 'VALIDATING' || p.status.code === 'FINALIZING'
                    ).length}
                  </p>
                  <p className="!text-xs !text-gray-600">처리중</p>
                </div>
                <div className="!text-center !border-r !border-gray-200">
                  <p className="!text-2xl !font-bold !text-green-600">
                    {orderDetail.contractPromptProcesses.filter(p => 
                      p.status.code === 'SUCCESS' || p.status.code === 'COMPLETED'
                    ).length}
                  </p>
                  <p className="!text-xs !text-gray-600">성공</p>
                </div>
                <div className="!text-center">
                  <p className="!text-2xl !font-bold !text-red-600">
                    {orderDetail.contractPromptProcesses.filter(p => 
                      p.status.code === 'FAIL' || p.status.code === 'FAILED' || 
                      p.status.code === 'EXPIRED' || p.status.code === 'CANCELLED'
                    ).length}
                  </p>
                  <p className="!text-xs !text-gray-600">실패</p>
                </div>
              </div>
            </div>

            {/* 프롬프트 목록 */}
            <div className="!flex-1 !overflow-y-auto !px-5 !py-4">
              <div className="!space-y-3">
                {orderDetail.contractPromptProcesses.map(process => (
                  <OFDPrompt key={process.id} process={process} />
                ))}
              </div>
            </div>

            {/* 하단 액션 영역 */}
            {orderDetail.contractPromptProcesses.some(p => p.status.code === 'FAIL') && (
              <div className="!px-5 !py-4 !bg-gray-50 !border-t !border-gray-200">
                <button
                  onClick={onClickRetryAllFailedPrompt}
                  className="!w-full !px-4 !py-2.5 !bg-red-600 hover:!bg-red-700 !text-white !text-sm !font-medium !rounded-lg !transition-colors !flex !items-center !justify-center !gap-2"
                >
                  <svg className="!w-4 !h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                  </svg>
                  실패한 프롬프트 모두 재시도
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default OrderListDetail;
